import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { PiFirstAidKitLight, PiPhoneCallLight, PiMapPinLight, PiClockLight } from 'react-icons/pi';

const getPlainText = (value) => {
  if (typeof value !== 'string') return '';

  const parsed = new DOMParser().parseFromString(value, 'text/html');
  return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
};

export default function Pharmacies() {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentDate = new Date().toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long'
  });

  useEffect(() => {
    fetchPharmacies();
  }, []);

  const fetchPharmacies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const rawBaseUrl = process.env.REACT_APP_API_URL || 'https://api.iskenderun.bel.tr/api';
      const baseUrl = rawBaseUrl.endsWith('/api') ? rawBaseUrl : `${rawBaseUrl}/api`;
      
      const response = await axios.get(`${baseUrl}/Diag/pharmacies`);
      if (response.data && response.data.isSuccess && response.data.data) {
        setPharmacies(response.data.data);
      } else {
        setError('Nöbetçi eczaneler yüklenemedi.');
      }
    } catch (err) {
      console.error('Error fetching pharmacies:', err);
      setError('Bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      margin: '0 auto', 
      width: '100%', 
      maxWidth: '480px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Premium Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        padding: '0.5rem 0'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#ef4444',
          marginBottom: '0.75rem',
          fontSize: '28px'
        }}>
          <PiFirstAidKitLight />
        </div>
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#0f172a',
          margin: '0 0 0.25rem 0'
        }}>
          Nöbetçi Eczaneler
        </h3>
        <p style={{
          fontSize: '0.9rem',
          color: '#64748b',
          margin: 0,
          fontWeight: '500'
        }}>
          {currentDate}
        </p>
      </div>

      {/* Main List Container */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {loading ? (
          /* Premium Skeleton Loader */
          [1, 2].map((i) => (
            <div key={i} style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              animation: 'pulse 1.5s infinite ease-in-out'
            }}>
              <div style={{ height: '20px', width: '50%', backgroundColor: '#f1f5f9', borderRadius: '4px' }} />
              <div style={{ height: '16px', width: '35%', backgroundColor: '#f1f5f9', borderRadius: '4px' }} />
              <div style={{ height: '32px', width: '100%', backgroundColor: '#f1f5f9', borderRadius: '4px', marginTop: '0.25rem' }} />
            </div>
          ))
        ) : error ? (
          <div style={{
            padding: '1.25rem',
            borderRadius: '16px',
            background: '#fef2f2',
            border: '1px solid #fee2e2',
            color: '#ef4444',
            textAlign: 'center',
            fontSize: '0.95rem',
            fontWeight: '500'
          }}>
            {error}
          </div>
        ) : pharmacies.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '1rem'
          }}>
            Aktif nöbetçi eczane bilgisi bulunamadı.
          </div>
        ) : (
          /* Native Duty Pharmacy Cards */
          pharmacies.map((pharmacy, idx) => (
            <div 
              key={idx}
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '1.25rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <h4 style={{
                  margin: 0,
                  fontSize: '1.15rem',
                  fontWeight: '700',
                  color: '#0f172a'
                }}>
                  {pharmacy.name}
                </h4>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  padding: '3px 8px',
                  borderRadius: '99px',
                  textTransform: 'uppercase'
                }}>
                  <PiClockLight /> Nöbetçi
                </span>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: '#475569'
              }}>
                {/* Clickable Phone Number */}
                <a 
                  href={`tel:${pharmacy.phone.replace(/[^0-9]/g, '')}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#0f75bc',
                    textDecoration: 'none',
                    fontWeight: '600',
                    width: 'fit-content'
                  }}
                >
                  <PiPhoneCallLight size={18} />
                  {pharmacy.phone}
                </a>

                {/* Address */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  lineHeight: '1.4'
                }}>
                  <PiMapPinLight size={18} style={{ color: '#64748b', flexShrink: 0, marginTop: '2px' }} />
                  <span>{getPlainText(pharmacy.address)}</span>
                </div>
              </div>

              {/* Navigation button */}
              {pharmacy.mapUrl && (
                <a 
                  href={pharmacy.mapUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    textDecoration: 'none',
                    color: '#fff',
                    background: '#0f75bc',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    padding: '0.6rem 1rem',
                    borderRadius: '10px',
                    marginTop: '0.5rem',
                    transition: 'background-color 0.2s ease',
                    boxShadow: '0 4px 6px -1px rgba(15, 117, 188, 0.15)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0b5f99';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f75bc';
                  }}
                >
                  <PiMapPinLight size={16} />
                  Yol Tarifi Al
                </a>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
